import { arrivalIn, metres, type Route, type Spot, type Stop } from '@day';
import { createStore } from './store';
import { roughDrive } from '../data/routing';

/** The dialog showing, if any: Try again on a stop, or a hand-in to confirm or record. */
export type Dialog =
  | { kind: 'tryAgain'; key: string }
  | { kind: 'handIn'; riderId: string; handInId?: string; out?: boolean }
  | { kind: 'startPlace' }
  /** A stop typed in for the waiting list (docs/waiting-stops.md). */
  | { kind: 'addWaiting' }
  /** Another day for waiting stops than the menu offers. */
  | { kind: 'waitingDay'; ids: string[] }
  /** A deliver-by time for stops in one go: on routes waiting to start ([keys]), or waiting for a route ([ids]). */
  | { kind: 'deliverBy'; keys?: string[]; ids?: string[] }
  /** A courier invited from the web (inviteCreate). */
  | { kind: 'invite' }
  /** The short name the business knows a courier by. */
  | { kind: 'shortName'; riderId: string }
  /** The note for a whole route (control/day-note). */
  | { kind: 'routeNote'; routeId: string }
  /** A stop's photo and details as one picture (proof/share-proof). */
  | { kind: 'proof'; key: string };

export const dialogs = createStore<{ open: Dialog | null }>({ open: null });
export const openDialog = (d: Dialog) => dialogs.set({ open: d });
export const closeDialog = () => dialogs.set({ open: null });

// ── Try again and handovers, worked out as the owner's phone does (lib/services/routes/handover.dart) ──

/** Roughly what a stop adds to a running route: into its cheapest gap after the stop being driven to (`roughlyAdds`). */
export function roughlyAdds(r: Route, x: Spot): number {
  const ahead = r.stops.slice(r.current).filter((s) => s.spot);
  let best = Infinity;
  const here = r.position ?? (r.current > 0 ? r.stops[r.current - 1]?.spot : null) ?? r.start;
  for (let i = 0; i <= ahead.length; i++) {
    const a = i === 0 ? here : ahead[i - 1].spot;
    const b = ahead[i]?.spot ?? null;
    const added = (a ? metres(a, x) : 0) + (b ? metres(x, b) - (a ? metres(a, b) : 0) : 0);
    best = Math.min(best, added);
  }
  return (roughDrive(Number.isFinite(best) ? best : 0) + r.timePerStopS) * 1000;
}

/** Bring it back: the shop as the courier's last stop, roughly (`roughlyAddsAtEnd`). */
export function roughlyAddsAtEnd(r: Route, shop: Spot): number {
  const last = [...r.stops].reverse().find((s) => s.spot)?.spot ?? r.position;
  return last ? roughDrive(metres(last, shop)) * 1000 : 0;
}

/** A place two couriers could meet: one of the holder's next stops (no detour), or the shop (a detour). */
export interface MeetOption {
  label: string;
  at: Spot;
  holderStopId: string | null;
  /** When the holder gets there, from now (ms). */
  holderIn: number;
  /** What it adds to the holder's day (ms). */
  holderAdds: number;
}

export function meetOptions(holder: Route, now: number, shop: (Spot & { label: string }) | null, max = 3): MeetOption[] {
  const out: MeetOption[] = [];
  for (let i = holder.current; i < holder.stops.length && out.length < max; i++) {
    const s = holder.stops[i];
    if (!s.spot || s.handover) continue;
    out.push({ label: s.street, at: s.spot, holderStopId: s.id, holderIn: arrivalIn(holder, i, now), holderAdds: 0 });
  }
  const at = holder.position;
  if (shop && at) {
    const next = holder.stops[holder.current]?.spot ?? null;
    const to = roughDrive(metres(at, shop)) * 1000;
    let adds = to;
    if (next) adds += (roughDrive(metres(shop, next)) - roughDrive(metres(at, next))) * 1000;
    out.push({ label: shop.label, at: { lat: shop.lat, lng: shop.lng }, holderStopId: null, holderIn: to, holderAdds: Math.max(0, adds) });
  }
  return out;
}

export interface MeetPlan {
  option: MeetOption;
  takerIn: number;
  takerAdds: number;
  wait: number;
}

/** The meeting place that adds the least for both couriers, waiting included, by straight lines (`roughMeet`). */
export function roughMeet(options: MeetOption[], from: Spot, next: Spot | null, customer: Spot, atStops: number): MeetPlan | null {
  let best: MeetPlan | null = null;
  let bestScore = Infinity;
  const back = next ? roughDrive(metres(customer, next)) * 1000 : 0;
  const skipped = next ? roughDrive(metres(from, next)) * 1000 : 0;
  for (const o of options) {
    const there = roughDrive(metres(from, o.at)) * 1000;
    const on = roughDrive(metres(o.at, customer)) * 1000;
    const adds = Math.max(0, there + on + back - skipped + atStops);
    const wait = Math.abs(o.holderIn - there);
    const score = adds + o.holderAdds + wait;
    if (score < bestScore) {
      bestScore = score;
      best = { option: o, takerIn: there, takerAdds: adds, wait };
    }
  }
  return best;
}

export type { Stop };
